'use client';
import React from 'react';
import Link from 'next/link';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/all';
import clsx from 'clsx';
import { Icon } from '@/components/Icon';
import { integrationIcons, flexibleIcon } from '@/sections/develocity/CiIntegrationSection/CiIntegrationSection.copy';
import { useGSAP } from '@gsap/react';
import { StaticImageData } from 'next/image';

interface IconScrollerProps {
  className?: string;
  src?: string;
  alt?: string;
  href?: string;
  width?: number;
  height?: number;
}

const IconScrollerAnimation = (ref: React.RefObject<HTMLDivElement | null>) => {
  gsap.registerPlugin(ScrollTrigger);

  if (!ref.current) return;

  const screen1 = ref.current.querySelector('.screen-1');
  const screen2 = ref.current.querySelector('.screen-2');
  if (!screen1 || !screen2) return;

  gsap
    .timeline({
      defaults: {
        ease: 'none',
        duration: 1,
      },
      scrollTrigger: {
        trigger: screen1,
        start: 'top+=50% top',
        end: 'bottom top',
        markers: false,
        scrub: 1,
      },
    })
    .fromTo(
      ref.current,
      {
        backgroundColor: '#fff',
      },
      {
        backgroundColor: '#2E3135',
      },
    );

  gsap
    .timeline({
      defaults: {
        ease: 'none',
        duration: 1,
      },
      scrollTrigger: {
        trigger: ref.current,
        start: 'top bottom',
        end: 'bottom bottom',
        scrub: true,
        markers: false,
      },
    })
    .to(
      screen1.querySelector('.icon-github'),
      {
        y: -screen1.clientHeight / 5,
        duration: 2,
      },
      0,
    )
    .to(
      screen1.querySelector('.icon-gitlab'),
      {
        y: -screen1.clientHeight / 4,
        duration: 2,
      },
      0,
    )
    .to(
      screen1.querySelector('.icon-jenkins'),
      {
        y: -screen1.clientHeight / 2,
        duration: 2,
      },
      0,
    )
    .to(
      screen1.querySelector('.icon-travisci'),
      {
        y: -screen1.clientHeight / 4,
        duration: 2,
      },
      0.5,
    )
    .to(
      screen1.querySelector('.icon-jetbrains'),
      {
        y: -screen1.clientHeight / 4,
        duration: 2,
      },
      0.5,
    )
    .to(
      screen1.querySelector('.icon-circleci'),
      {
        y: -screen1.clientHeight / 4,
        duration: 2,
      },
      0.5,
    );

  // ICON SCREEN 2

  gsap
    .timeline({
      defaults: {
        ease: 'none',
        duration: 1,
      },
      scrollTrigger: {
        trigger: ref.current,
        start: 'top bottom',
        end: 'bottom bottom',
        scrub: 0.5,
        markers: false,
      },
    })
    .to(
      screen2.querySelector('.icon-alibaba'),
      {
        y: -screen2.clientHeight / 5,
        duration: 2,
      },
      0,
    )
    .to(
      screen2.querySelector('.icon-aws'),
      {
        y: -screen2.clientHeight / 2,
        duration: 2,
      },
      0,
    )
    .to(
      screen2.querySelector('.icon-gcm'),
      {
        y: -screen2.clientHeight / 4,
        duration: 2,
      },
      0.5,
    )
    .to(
      screen2.querySelector('.icon-anchor'),
      {
        y: -screen2.clientHeight / 4.2,
        duration: 2,
      },
      0.5,
    )
    .to(
      screen2.querySelector('.icon-autodesk'),
      {
        y: -screen2.clientHeight / 4.3,
        duration: 2,
      },
      0.5,
    )
    .to(
      screen2.querySelector('.icon-google_cloud'),
      {
        y: -screen1.clientHeight / 4.5,
        duration: 2,
      },
      0.5,
    )
    .to(
      screen2.querySelector('.icon-docker'),
      {
        y: -screen2.clientHeight / 4.7,
        duration: 2,
      },
      0.5,
    )
    .to(
      screen2.querySelector('.icon-openshift'),
      {
        y: -screen2.clientHeight / 4.1,
        duration: 2,
      },
      0.5,
    )
    .to(
      screen2.querySelector('.icon-stack'),
      {
        y: -screen2.clientHeight / 4,
        duration: 2,
      },
      0.5,
    )
    .to(
      screen2.querySelector('.icon-rancher'),
      {
        y: -screen2.clientHeight / 4.5,
        duration: 2,
      },
      0.5,
    );

  // TEXT ANIMATION
  gsap
    .timeline({
      defaults: {
        ease: 'none',
        duration: 1,
      },
      scrollTrigger: {
        trigger: screen1,
        start: 'top center',
        end: 'bottom center',
        markers: false,
        scrub: 0.5,
      },
    })

    .fromTo(
      screen1.querySelector('h2'),
      {
        y: 100,
        alpha: 0,
      },
      {
        y: 0,
        alpha: 1,
      },
    )
    .fromTo(
      screen1.querySelector('p'),
      {
        y: 100,
        alpha: 0,
      },
      {
        y: 0,
        alpha: 1,
      },
    )
    .fromTo(
      screen1.querySelector('a'),
      {
        y: 100,
        alpha: 0,
      },
      {
        y: 0,
        alpha: 1,
      },
      '-=1',
    )
    .to(screen1.querySelector('.text'), {
      alpha: 1,
      duration: 3,
    });

  gsap
    .timeline({
      defaults: {
        ease: 'none',
        duration: 1,
      },
      scrollTrigger: {
        trigger: screen2,
        start: 'top center',
        end: 'bottom center',
        markers: false,
        scrub: 0.5,
      },
    })

    .fromTo(
      screen2.querySelector('h2'),
      {
        y: 100,
        alpha: 0,
      },
      {
        y: 0,
        alpha: 1,
      },
    )
    .fromTo(
      screen2.querySelector('p'),
      {
        y: 100,
        alpha: 0,
      },
      {
        y: 0,
        alpha: 1,
      },
    )
    .fromTo(
      screen2.querySelector('a'),
      {
        y: 100,
        alpha: 0,
      },
      {
        y: 0,
        alpha: 1,
      },
      '-=1',
    )
    .to(screen2.querySelector('.text'), {
      alpha: 1,
      duration: 3,
    });
};

interface IconProps {
  style?: string;
  src: string | StaticImageData;
  alt?: string;
  href?: string;
  width?: number;
  height?: number;
  index?: string;
  theme?: string;
}

const renderIcon = ({ src, width, height, alt, style, index, theme }: IconProps) => {
  const themeStyle = clsx({
    'bg-light-background-200 shadow-card': theme === 'light',
    'bg-dark-background shadow-card-dark': theme === 'dark',
  });

  return (
    <Icon
      className={`absolute scale-[0.35] transform p-6 md:scale-50 lg:scale-75 ${style} ${themeStyle}`}
      src={src}
      width={width}
      height={height}
      alt={alt}
      key={index}
    />
  );
};

export const CiIntegrationSection: React.FC<IconScrollerProps> = () => {
  const ref = React.useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      IconScrollerAnimation(ref);
    },
    { scope: ref },
  );
  return (
    <div className=" relative flex  w-full flex-col items-center justify-center overflow-hidden" ref={ref}>
      <div className="screen-1 flex h-screen w-full flex-col items-center justify-center ">
        <div className="text container z-10 flex w-full max-w-xl flex-col items-center justify-center text-center md:w-auto">
          {' '}
          <h2 className="text-h3 mb-6 text-gray-1">
            Develocity integrates with all <span className="text-gradle-blue">CI systems </span>
          </h2>
          <p className="text-body mb-8 text-gray-3">
            Develocity offers plug-ins and templates for a convenient and feature-rich integration with Bamboo, GitHub
            Actions, GitLab, Jenkins CI, and TeamCity. Take your CI throughput and observability to the next level.
            Develocity is also an indispensable tool for CI migration.
          </p>
          <Link
            href={`/develocity/integrations`}
            className="text-body underline-text-transition-blue group flex items-center justify-center justify-center gap-1 text-gray-2"
          >
            Learn More
            <ChevronRightIcon className="inline-block size-6 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="absolute left-0 top-0 flex h-screen w-full flex-col items-center justify-center">
          {integrationIcons.map((icon, index) =>
            renderIcon({
              ...icon,
              index: `${icon.alt + index}`,
              theme: 'light',
            }),
          )}
        </div>
      </div>
      <div className="screen-2 relative flex h-screen w-full flex-col items-center justify-center">
        <div className="text container relative z-20 flex max-w-xl flex-col items-center justify-center text-center text-white">
          <h2 className="text-h3 mb-6">
            Develocity is compatible with all popular <span className="text-gradle-blue">deployment</span>
            <p className="text-gradle-blue">environments</p>
          </h2>
          <p className="text-body mb-8">
            Develocity is flexible to deploy and integrates easily with your existing application deployment
            infrastructure.
          </p>
          <Link
            href={`/develocity/integrations`}
            className="text-body underline-text-transition-blue group flex items-center justify-center gap-1"
          >
            Learn More
            <ChevronRightIcon className="inline-block size-6 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="absolute left-0 top-0 z-0 flex h-screen w-full flex-col items-center justify-center">
          {flexibleIcon.map((icon, index) =>
            renderIcon({
              ...icon,
              index: `${icon.alt + index}`,
              theme: 'dark',
            }),
          )}
        </div>
      </div>
    </div>
  );
};
